import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutAction } from './authSlice';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Idle / session-timeout guard.
 *
 * While the user is signed in, this watches for activity (mouse, keyboard,
 * touch, scroll). After `idleMinutes` of NO activity it pops a warning modal
 * with a countdown:
 *   • "Continue" (OK) → keeps the session alive by hitting `authService.me()`
 *     — which makes the apiClient interceptor proactively refresh the token if
 *     it's near expiry — then resets the idle clock.
 *   • "Log out" OR the countdown reaching zero → best-effort server revoke +
 *     `logout('expired')`, so RequireAuth redirects to /login and the
 *     SessionEndedBanner explains the inactivity sign-out.
 *
 * While the warning is open, ordinary activity does NOT auto-dismiss it — the
 * user must explicitly click Continue (per the requirement: "if click ok then
 * continue … if not then logout").
 *
 * Mount once at app root inside the Redux <Provider> (alongside the session
 * bootstrap). Renders nothing until the warning fires.
 */

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'wheel'] as const;
const ACTIVITY_THROTTLE_MS = 1000;

export interface SessionTimeoutGuardProps {
  /** Minutes of inactivity before the warning modal appears. Default 15. */
  idleMinutes?: number;
  /** Seconds the warning counts down before auto sign-out. Default 60. */
  warnSeconds?: number;
}

export function SessionTimeoutGuard({ idleMinutes = 15, warnSeconds = 60 }: SessionTimeoutGuardProps) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootWithAuth) => s.auth.isAuthenticated);
  const sessionId = useSelector((s: RootWithAuth) => s.auth.session?.sessionId);

  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(warnSeconds);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const countdown = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const warningRef = useRef(false); // readable inside event handlers without re-subscribing
  const busy = useRef(false);
  // Latest signOut closure, read from the timer so the timer callback never
  // captures a stale one (and the timer setup needn't depend on it).
  const signOutRef = useRef<() => void | Promise<void>>(() => {});

  const stopCountdown = useCallback(() => {
    if (countdown.current) { clearInterval(countdown.current); countdown.current = undefined; }
  }, []);

  const armIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    stopCountdown();
    idleTimer.current = setTimeout(() => {
      warningRef.current = true;
      // Drive the countdown off an absolute deadline + a single interval started
      // right here — robust against re-renders / effect re-runs.
      const deadline = Date.now() + Math.max(5, warnSeconds) * 1000;
      setRemaining(warnSeconds);
      setWarning(true);
      stopCountdown();
      countdown.current = setInterval(() => {
        const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        setRemaining(left);
        if (left <= 0) { stopCountdown(); void signOutRef.current(); }
      }, 1000);
    }, Math.max(0.01, idleMinutes) * 60 * 1000);
  }, [idleMinutes, warnSeconds, stopCountdown]);

  const signOut = useCallback(async () => {
    stopCountdown();
    if (idleTimer.current) clearTimeout(idleTimer.current);
    warningRef.current = false;
    setWarning(false);
    try {
      const api = await import('@kss/api');
      if (sessionId) await api.authService.revoke({ sessionId });
    } catch { /* proceed with local logout regardless */ }
    dispatch(logoutAction('expired'));
  }, [dispatch, sessionId, stopCountdown]);
  // Keep the timer's view of signOut current without re-arming the timers.
  useEffect(() => { signOutRef.current = signOut; }, [signOut]);

  const staySignedIn = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    stopCountdown();
    try {
      // Keep-alive: a lightweight authed call. The apiClient interceptor
      // proactively refreshes the token if it's inside the expiry leeway, so
      // this both extends the server session and rotates the JWT when needed.
      const api = await import('@kss/api');
      await api.authService.me();
    } catch {
      /* if this 401s the interceptor already attempted refresh; the periodic
         drift check / next request will force logout if it truly failed. */
    } finally {
      busy.current = false;
      warningRef.current = false;
      setWarning(false);
      armIdle();
    }
  }, [armIdle, stopCountdown]);

  // Activity wiring + idle arming (only while authenticated).
  useEffect(() => {
    if (!isAuthenticated) {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      stopCountdown();
      warningRef.current = false;
      setWarning(false);
      return;
    }
    armIdle();
    let last = 0;
    const onActivity = () => {
      if (warningRef.current) return; // don't auto-dismiss the warning
      const now = Date.now();
      if (now - last < ACTIVITY_THROTTLE_MS) return;
      last = now;
      armIdle();
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      stopCountdown();
    };
  }, [isAuthenticated, armIdle, stopCountdown]);

  if (!isAuthenticated || !warning) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kss-session-timeout-title"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-2xl">
        <h2 id="kss-session-timeout-title" className="text-base font-semibold text-[var(--color-text-primary)]">
          Session about to expire
        </h2>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          You&rsquo;ve been inactive. For your security you&rsquo;ll be signed out in{' '}
          <b className="text-[var(--color-text-primary)]">{remaining}s</b>. Do you want to stay signed in?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
          >
            Log out
          </button>
          <button
            type="button"
            onClick={() => void staySignedIn()}
            autoFocus
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-[var(--color-primary-fg,#fff)] hover:opacity-90"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeoutGuard;
